// services/admin.ts
// Wrapper fino sobre as rotas de `/admin/*`. Não gerencia estado nem headers:
// o `X-Admin-Email` é injetado centralmente pelo `useAdminApi` (ver hook).
import { AuthFetch } from "../hooks/useApi";
import {
  AdminStatus,
  AdminDoctor,
  AdminAccessListParams,
  AdminAccessListResponse,
  AdminAccessCreatePayload,
  AdminAccessUpdatePayload,
  AdminRevokeResponse,
} from "../types/admin.types";

function buildAccessQuery(params: AdminAccessListParams): string {
  const search = new URLSearchParams();
  if (params.only_allowed) search.set("only_allowed", "true");
  if (params.only_admins) search.set("only_admins", "true");
  if (params.q?.trim()) search.set("q", params.q.trim());
  if (params.limit != null) search.set("limit", String(params.limit));
  if (params.offset != null) search.set("offset", String(params.offset));
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export const adminService = {

  // Gate da página. Só exige `Authorization` (não exige `X-Admin-Email`).
  getStatus: (
    apiFetch: AuthFetch,
    email: string,
  ): Promise<AdminStatus> =>
    apiFetch<AdminStatus>(`/admin/status?email=${encodeURIComponent(email)}`, {
      method: "GET",
    }),

  listAccess: (
    apiFetch: AuthFetch,
    params: AdminAccessListParams = {},
  ): Promise<AdminAccessListResponse> =>
    apiFetch<AdminAccessListResponse>(`/admin/access${buildAccessQuery(params)}`, {
      method: "GET",
    }),

  getAccess: (
    apiFetch: AuthFetch,
    email: string,
  ): Promise<AdminDoctor> =>
    apiFetch<AdminDoctor>(`/admin/access/${encodeURIComponent(email)}`, {
      method: "GET",
    }),

  // Upsert pelo `email`: cria se não existir, atualiza se existir. Resposta 201.
  createAccess: (
    apiFetch: AuthFetch,
    payload: AdminAccessCreatePayload,
  ): Promise<AdminDoctor> =>
    apiFetch<AdminDoctor>(`/admin/access`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  // Envie só os campos alterados.
  updateAccess: (
    apiFetch: AuthFetch,
    email: string,
    payload: AdminAccessUpdatePayload,
  ): Promise<AdminDoctor> =>
    apiFetch<AdminDoctor>(`/admin/access/${encodeURIComponent(email)}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  // Revogação soft: revoga as concessões da organização do médico.
  revokeAccess: (
    apiFetch: AuthFetch,
    email: string,
  ): Promise<AdminRevokeResponse> =>
    apiFetch<AdminRevokeResponse>(`/admin/access/${encodeURIComponent(email)}`, {
      method: "DELETE",
    }),

  // Promover / rebaixar admin.
  setAdmin: (
    apiFetch: AuthFetch,
    email: string,
    isAdmin: boolean,
  ): Promise<AdminDoctor> =>
    apiFetch<AdminDoctor>(`/admin/admins/${encodeURIComponent(email)}`, {
      method: "PATCH",
      body: JSON.stringify({ is_admin: isAdmin }),
    }),
};
